/**
 * Acknowledgment sound catalog (HandsFree submit tick, companion finished-run chime).
 *
 * ## Where to find more sounds (listen and choose)
 *
 * **Pre-built UI / earcon libraries (royalty-free or commercial):**
 * - **Pixabay Sound Effects** – https://pixabay.com/sound-effects/search/ui
 *   Royalty-free UI/notification sounds (WAV/MP3). Free for commercial use.
 * - **Uppbeat** – https://uppbeat.io/sfx/category/digital-and-ui/ui
 *   Free UI SFX with filters; check license per sound.
 * - **Dev_Tones (rcptones.com)** – https://rcptones.com/dev_tones/
 *   UI sound library (600+), Material-style; free examples + paid pack.
 * - **ElevenLabs Sound Effects** – https://elevenlabs.io/sound-effects/notification
 *   AI-generated notification SFX; WAV/MP3, commercial use (attribution on free).
 * - **Google Assistant Sound Library** – https://developers.google.com/assistant/tools/sound-library
 *   Earcons for Actions on Google only; not for general web use.
 *
 * **To use a pre-built file:** add the file under `public/` or your app’s static assets,
 * add a new id to `AckSoundId` and an entry in `ACK_SOUND_CONFIG`
 * with `type: 'asset'` and `url`.
 */

/** Sound IDs for "message submitted" acknowledgment. Add new IDs here when you add options. */
export const ACK_SOUND_IDS = [
  'submit_soft_tick',
  'submit_blip',
  'submit_gentle',
  'submit_thunk',
  'submit_whisper',
  'submit_confirm',
  'chime_major_third',
  'chime_fifth',
  'chime_fourth',
  'chime_quick_double',
] as const;

export type AckSoundId = (typeof ACK_SOUND_IDS)[number];

/** Played when no id is passed — the HandsFree "Next" submit tick. Change here to switch. */
export const DEFAULT_ACK_SOUND: AckSoundId = 'submit_soft_tick';

/** Single soft tick — subtle "message sent" feel (iMessage, Slack, etc.) */
interface TickPreset {
  type: 'tick';
  freq: number;
  dur: number; // seconds
  gain: number;
}

/** Two-tone chime. */
interface ChimePreset {
  type: 'chime';
  tone1Freq: number;
  tone2Freq: number;
  gainLevel: number;
  tone1Dur: number;
  gap: number;
  tone2Dur: number;
}

/** Pre-built asset from public/ or CDN */
interface AssetPreset {
  type: 'asset';
  url: string;
}

export type AckSoundPreset = TickPreset | ChimePreset | AssetPreset;

/**
 * Catalog of "message submitted" sounds. Subtle tick presets feel like
 * iMessage/Slack send feedback; chime presets are more melodic.
 */
export const ACK_SOUND_CONFIG: Record<AckSoundId, AckSoundPreset> = {
  // Subtle single-tone "send" sounds (short, audible)
  submit_soft_tick: {
    type: 'tick',
    freq: 980,
    dur: 0.04,
    gain: 1,
  },
  submit_blip: {
    type: 'tick',
    freq: 1200,
    dur: 0.032,
    gain: 0.18,
  },
  submit_gentle: {
    type: 'tick',
    freq: 660,
    dur: 0.045,
    gain: 0.19,
  },
  submit_thunk: {
    type: 'tick',
    freq: 320,
    dur: 0.05,
    gain: 0.22,
  },
  submit_whisper: {
    type: 'tick',
    freq: 1400,
    dur: 0.028,
    gain: 0.16,
  },
  submit_confirm: {
    type: 'tick',
    freq: 740,
    dur: 0.038,
    gain: 0.2,
  },
  // Two-tone chimes
  chime_major_third: {
    type: 'chime',
    tone1Freq: 523.25,
    tone2Freq: 659.25,
    gainLevel: 0.24,
    tone1Dur: 0.07,
    gap: 0.03,
    tone2Dur: 0.09,
  },
  chime_fifth: {
    type: 'chime',
    tone1Freq: 523.25,
    tone2Freq: 783.99,
    gainLevel: 0.22,
    tone1Dur: 0.065,
    gap: 0.025,
    tone2Dur: 0.085,
  },
  chime_fourth: {
    type: 'chime',
    tone1Freq: 523.25,
    tone2Freq: 698.46,
    gainLevel: 0.23,
    tone1Dur: 0.065,
    gap: 0.028,
    tone2Dur: 0.088,
  },
  chime_quick_double: {
    type: 'chime',
    tone1Freq: 587.33,
    tone2Freq: 587.33,
    gainLevel: 0.21,
    tone1Dur: 0.05,
    gap: 0.04,
    tone2Dur: 0.05,
  },
};
